/**
 * Kurs-Screen — 1:1 zur Web-App (modul.html + app.js), mobil als Drawer + Player.
 * Verhalten nach docs/analyse/VERHALTENS-PARITAET_Web-vs-App_2026-06.md:
 *
 *  - Ohne Position: Modul-ERÖFFNUNGSSEITE von Modul 1 (NICHT Kapitel 1) — B3.
 *  - Drawer: alle Module zugeklappt; Name-Button → Intro, Pfeil → nur auf/zu — S1/S2.
 *  - Unterkapitel (Task F): h3-Split, „Abschnitt X/Y", klickbare Punkte; „Weiter"
 *    markiert gelesen + streamt, Punkte/Zurück nicht — U1–U6.
 *  - Werkstatt (m08): Intro unpaginiert + Projektliste; Projekte als Schritt-Seiten — U7/U8.
 *  - Vorlesen: Knopfreihe im Drawer + Schnell-Knopf im Kopf; harter Stopp bei
 *    Navigation; Auto/Durchlesen über die TTS-Bridge — §5.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { activeCourseId, loadCourse, loadChapter } from "../data/course-store";
import type { Chapter, Course as CourseType, CourseModule, QuestionsBlock, TextBlock } from "../data/types";
import { loadProgress, isDone, toggleDone, contentKey, sendProgressEvent, markSubRead, isSubRead } from "../state/progress";
import { courseMode, setCourseMode } from "../state/ui";
import { setContext, openSheet } from "../state/coach";
import { setPosition, lastPosition, jumpRequest } from "../state/course-nav";
import { isTrainer } from "../state/session";
import { resolveModes, getMode } from "../modes/mode-registry";
import { assetUrl } from "../data/assets";
import { LessonStream } from "../content/LessonStream";
import { sanitizeHtml } from "../content/sanitize";
import { useScrollHint } from "../content/use-scroll-hint";
import { splitInUnterkapitel, type UkSegment } from "../content/unterkapitel";
import { projektSegmente, WerkstattUebersicht, istWerkstattIntro, modulStartChapter, type ProjektPayload } from "../content/werkstatt";
import { wkRedraw } from "../content/werkstatt-personal";
import { TokenBalken } from "../app/BrandHeader";
import { QuestionCard } from "../content/QuestionCard";
import { MediaFigure } from "../content/MediaFigure";
import { Modal } from "../ui/Modal";
import { useBackClose, overlayIsOpen } from "../ui/use-back-close";
import { setTtsBridge, setTtsPlayFallback, onNavigateStart as ttsNavigateStart, onChapterReady as ttsChapterReady, onUserNav as ttsUserNav, stopp as ttsStopp, spieleAb as ttsPlay, ttsAudioOn, ttsPlaying, ttsWaiting, ttsPaused, pause as ttsPause, fortsetzen as ttsFortsetzen, playFromSection as ttsPlayFromSection } from "../tts/player";
import { showToast } from "../ui/toast";
import { TtsQuick } from "../tts/TtsBar";
import { fontScalePref, FONT_SCALE_ORDER, FONT_SCALE_SHORT, setFontScale } from "../state/fontscale";
import { IconArrowLeft, IconArrowRight, IconSparkles, IconBook, IconMenu, IconClose, IconHome, IconLibrary } from "../app/icons";

interface Flat {
  m: string;
  b: string;
}

/** Anzeige-Modulnummer: nur Teilnehmer-Module (menueNr); Trainer/Spezial ohne Nummer. */
const modNumPrefix = (m: { menueNr?: number | null } | null | undefined): string =>
  m && m.menueNr != null ? `Modul ${m.menueNr} · ` : "";
const modLabel = (m: { menueNr?: number | null; title: string }): string =>
  `${modNumPrefix(m)}${m.title}`;

/** Abschnitts-Schlüssel je Kapitel — eine Quelle für Footer- UND Sidebar-Kreise.
 *  (Mobile-Abweichung §7: nur für bereits geladene Kapitel bekannt.) */
const segCache = new Map<string, UkSegment[]>();

/** Doppelte Kurzbeschreibung unterdrücken (Web-Parität A17/L16): wenn der Lesetext
 *  wörtlich mit der Summary beginnt, wird die Kopf-Summary ausgeblendet (sonst steht
 *  und liest TTS sie doppelt). Im Fragen-Modus/in Listen bleibt sie sichtbar. */
function summaryDoppelt(summary: string | undefined, html: string | undefined): boolean {
  const s = (summary || "").trim();
  if (!s || !html) return false;
  const div = document.createElement("div");
  div.innerHTML = html;
  const norm = (x: string) =>
    (x || "").toLowerCase().normalize("NFKC").replace(/[^a-z0-9äöüß]+/g, " ").replace(/\s+/g, " ").trim();
  const ns = norm(s);
  return !!ns && norm(div.textContent || "").slice(0, ns.length + 240).includes(ns);
}

/** Fenster-Scroll: ist die Seite (fast) bis unten gelesen? */
function scrolledBottom(): boolean {
  const el = (document.scrollingElement as HTMLElement) || document.documentElement;
  return el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
}

/**
 * Modul-Eröffnungsseite (Intro) — 1:1 zur Web-App: Headline, Eröffnungstext,
 * Video, Kapitelliste. „Modul/Kurs starten" springt bei Projekt-Modulen auf das
 * erste Nicht-Projekt-Kapitel (Web: modulStartBlock).
 */
function ModuleIntro({ mod, onStart }: { mod: CourseModule | null; onStart: (m: string, b: string) => void }) {
  if (!mod) return null;
  const bg = mod.intro?.image || mod.image;
  const v = mod.intro?.video;
  const start = modulStartChapter(mod);
  return (
    <section class="chapter-player glass" style={bg ? `--chapter-bg:url('${bg}')` : undefined}>
      {/* Video gehört in den Kopfbalken (Web-Parität 215e49d), nicht in den Body.
          Auf schmalem Viewport stapelt das CSS Text + Video (kein hartes Nebeneinander),
          und ohne Video gibt es keinen leeren Rahmen (v?.src-Guard). */}
      <header class={`chapter-header${v?.src ? " chapter-header--video" : ""}`}>
        <div class="chapter-header-text">
          {/* Kicker zeigt „Modul N · Titel" (Web-Parität 215e49d), damit man auf der
              Eröffnungsseite sofort sieht, in welchem Modul man ist. */}
          <p class="chapter-kicker">{modLabel(mod)}</p>
          <h1>{mod.intro?.headline || mod.title}</h1>
          {mod.summary ? <p class="chapter-summary">{mod.summary}</p> : null}
        </div>
        {v?.src ? (
          <figure class="chapter-header-video">
            <video src={v.src} poster={v.poster} controls preload="metadata" />
            {v.caption ? <figcaption>{v.caption}</figcaption> : null}
          </figure>
        ) : null}
      </header>
      <div class="chapter-scroll">
        {mod.intro?.textHtml ? (
          <div class="lesson glass" dangerouslySetInnerHTML={{ __html: sanitizeHtml(mod.intro.textHtml) }} />
        ) : (
          <div class="lesson glass">
            <p>{mod.summary || "Übersicht über dieses Modul."}</p>
          </div>
        )}
        <div class="module-intro-chapters">
          <h3 class="group-label">Kapitel in diesem Modul</h3>
          {mod.chapters.map((ch) => (
            <button key={ch.id} type="button" class="mi-chapter" onClick={() => onStart(mod.id, ch.id)}>
              <span class="dot" />
              <span class="mi-chapter-title">{ch.title}</span>
            </button>
          ))}
        </div>
      </div>
      <footer class="chapter-footer">
        <button
          class="chapter-next"
          disabled={!start}
          onClick={() => {
            if (start) onStart(mod.id, start.id);
          }}
        >
          {mod.intro?.ctaLabel || "Kurs starten"} <IconArrowRight size={16} />
        </button>
      </footer>
    </section>
  );
}

export function Course() {
  const { route } = useLocation();
  const courseId = activeCourseId.value;
  const [course, setCourse] = useState<CourseType | null>(null);
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [pos, setPos] = useState<Flat | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Drawer: ALLE Module starten zugeklappt (B2/S2); rein manuelles Auf-/Zuklappen.
  const [openMods, setOpenMods] = useState<string[]>([]);
  const [introMod, setIntroMod] = useState<string | null>(null);
  // Unterkapitel (Task F)
  const [segments, setSegments] = useState<UkSegment[]>([]);
  const [subIndex, setSubIndex] = useState(0);
  const [streamSub, setStreamSub] = useState(true); // „Weiter" streamt, Punkte/Zurück nicht (U5)
  const scrollRef = useRef<HTMLDivElement>(null);
  const reqIdRef = useRef(0);
  // Einzel-/Doppeltipp-Disambiguierung auf dem Lesetext (Auftraggeber 13.06.):
  // 1. Tipp wartet TAP_DBL_MS; kommt ein 2. Tipp → Doppeltipp (Pause/weiter),
  // sonst Einzeltipp (ab diesem Abschnitt lesen).
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Aktuelle Position als Refs — der popstate-Handler (Closure vom Mount) braucht
  // den frischen Stand, um „gleiche Stelle"-Pops (stale Overlay-Einträge) zu ignorieren.
  const posRef = useRef<Flat | null>(null);
  const subIndexRef = useRef(0);
  const introModRef = useRef<string | null>(null);

  // Android-Zurück bei offenem Seitenmenü: schließt NUR das Menü (Task F).
  useBackClose(drawerOpen, () => setDrawerOpen(false));
  // Moduswahl (Lesen/Fragen) nur im Grundlagenkurs m01 — 1:1 zur Web-App.
  posRef.current = pos;
  subIndexRef.current = subIndex;
  introModRef.current = introMod;
  const hatModuswahl = pos?.m === "m01";
  const mode = hatModuswahl ? courseMode.value : "normal";
  const showScrollHint = useScrollHint(`${pos?.m ?? ""}/${pos?.b ?? ""}/${subIndex}/${mode}/${introMod ?? ""}`);

  function toggleModule(id: string) {
    setOpenMods((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }
  /** Modulname → Eröffnungsseite (Pfeil klappt nur die Kapitel auf/zu). */
  function openIntro(id: string) {
    ttsUserNav();
    ttsNavigateStart();
    setDrawerOpen(false);
    setIntroMod(id);
    setChapter(null);
    setPos(null);
    // KEIN Auto-Aufklappen im Drawer (Auftraggeber: am Anfang nichts aufgeklappt;
    // nur das gerade GELESENE Kapitel-Modul wird beim Menü-Öffnen aufgeklappt).
    // Coach-Kontext aufs Modul stellen (Web: setActiveModule → block_id null).
    const m = course?.modules.find((x) => x.id === id);
    setContext({ courseId, moduleId: id, blockId: null, title: m ? modLabel(m) : "Modul" });
    try {
      history.pushState({ intro: id }, "", `/kurs?m=${encodeURIComponent(id)}&intro=1`);
    } catch {
      /* ignore */
    }
    // Eröffnungsseite oben starten (nach dem Rendern; Fenster scrollt).
    requestAnimationFrame(scrollTopNow);
    setTimeout(scrollTopNow, 0);
  }
  const moduleOpen = (id: string) => openMods.includes(id);

  const visibleModules = (c: CourseType | null) =>
    (c?.modules ?? []).filter((m) => m.group !== "trainer" || isTrainer.value);

  function flatChapters(c: CourseType | null): Flat[] {
    return visibleModules(c).flatMap((m) => m.chapters.map((ch) => ({ m: m.id, b: ch.id })));
  }

  function scrollTopNow() {
    // Die App scrollt am Fenster (Wurzel-Scroller), zusätzlich evtl. inneres
    // .chapter-scroll. Alle Kandidaten zurücksetzen — robust unter html{zoom}.
    const root = (document.scrollingElement as HTMLElement) || document.documentElement;
    try { root.scrollTop = 0; } catch { /* ignore */ }
    try { document.body.scrollTop = 0; } catch { /* ignore */ }
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
    document.querySelectorAll<HTMLElement>(".chapter-scroll").forEach((el) => (el.scrollTop = 0));
  }

  /** Kapitel → Abschnitte (U1/U7/U8): projekt-Segmente, h3-Split oder nichts. */
  function computeSegments(ch: Chapter, c: CourseType | null): UkSegment[] {
    const mod = c?.modules.find((x) => x.id === ch.moduleId);
    if (ch.layout === "projekt") return projektSegmente((ch.raw ?? {}) as ProjektPayload, ch.moduleId);
    if (istWerkstattIntro(mod, ch.id)) return []; // Projektliste hängt darunter (U7)
    const textBlock = ch.blocks.find((x) => x.type === "text") as TextBlock | undefined;
    return splitInUnterkapitel(textBlock?.html ?? "");
  }

  async function selectChapter(
    m: string,
    b: string,
    c: CourseType | null = course,
    pushHistory = true,
    // 0 = vorn anfangen (normale Klicks, wie Web); N = expliziter Abschnitt (?u=);
    // -1 = Resume auf ersten ungelesenen Abschnitt (nur „Fortsetzen"/letzte Position).
    wantedSub = 0,
    // Optionaler Abschnitts-SLUG (Chat-Sprunglinks `&u=<slug>`) — wird nach dem
    // Laden gegen die Segment-Keys aufgelöst und hat Vorrang vor wantedSub.
    wantedSubSlug?: string,
  ) {
    ttsNavigateStart();
    setDrawerOpen(false);
    setIntroMod(null);
    setPos({ m, b });
    setLoading(true);
    setErr(null);
    const myReq = ++reqIdRef.current;
    try {
      const ch = await loadChapter(m, b, courseId);
      if (myReq !== reqIdRef.current) return; // veraltetes Ergebnis verwerfen
      const segs = computeSegments(ch, c);
      segCache.set(`${m}/${b}`, segs);
      // Abschnitts-Start: explizit (?u=) > Resume (erster ungelesener) > 0.
      let si = 0;
      const slugIdx = wantedSubSlug ? segs.findIndex((s) => s.key === wantedSubSlug) : -1;
      if (slugIdx >= 0) si = slugIdx;
      else if (wantedSub >= 0) si = Math.min(Math.max(0, wantedSub), Math.max(0, segs.length - 1));
      else if (segs.length > 1 && !isDone(contentKey(m, b, "chapter", "chapter"))) {
        const idx = segs.findIndex((s) => !isSubRead(m, b, s.key));
        // Alles gelesen, Kapitel aber nicht abgehakt → ans Ende (ein „Weiter" zum nächsten).
        si = idx >= 0 ? idx : segs.length - 1;
      }
      setChapter(ch);
      setSegments(segs);
      setSubIndex(si);
      setStreamSub(true);
      setPosition(courseId, m, b, si);
      if (pushHistory) {
        try {
          const uq = si > 0 ? `&u=${si}` : "";
          const url = `/kurs?course=${encodeURIComponent(courseId)}&m=${encodeURIComponent(m)}${uq}#${encodeURIComponent(b)}`;
          history.pushState({ m, b, u: si }, "", url);
        } catch {
          /* ignore */
        }
      }
      const mod = c?.modules.find((x) => x.id === m);
      const title = `${modNumPrefix(mod)}${ch.title}`.trim();
      setContext({ courseId, moduleId: m, blockId: b, title });
      sendProgressEvent({
        courseId,
        moduleId: m,
        blockId: b,
        itemId: "chapter",
        itemType: "chapter",
        eventType: "chapter_opened",
      });
      // Auto-/Durchlesen darf erst starten, wenn Segmente + Bridge stehen.
      ttsChapterReady();
      requestAnimationFrame(scrollTopNow);
    } catch {
      if (myReq === reqIdRef.current) setErr("Kapitel konnte nicht geladen werden.");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }

  /** Abschnitt wechseln OHNE Kapitel neu zu laden (U5: stream nur bei „Weiter"). */
  function zeigeUnterkapitel(idx: number, stream: boolean) {
    if (!segments.length || !pos) return;
    const si = Math.max(0, Math.min(segments.length - 1, idx));
    setSubIndex(si);
    setStreamSub(stream);
    setPosition(courseId, pos.m, pos.b, si);
    try {
      const uq = si > 0 ? `&u=${si}` : "";
      history.replaceState({ m: pos.m, b: pos.b, u: si }, "", `/kurs?course=${encodeURIComponent(courseId)}&m=${encodeURIComponent(pos.m)}${uq}#${encodeURIComponent(pos.b)}`);
    } catch {
      /* ignore */
    }
    scrollTopNow();
    requestAnimationFrame(scrollTopNow);
  }

  function posFromUrl(c: CourseType): (Flat & { u: number; uSlug?: string; intro?: string | null }) | null {
    try {
      const params = new URLSearchParams(location.search);
      const m = params.get("m");
      if (params.get("intro") && m && c.modules.some((x) => x.id === m)) {
        return { m, b: "", u: 0, intro: m };
      }
      const b = params.get("b") || decodeURIComponent(location.hash.replace(/^#/, ""));
      const uParam = params.get("u") || "";
      // u=N (STRIKT numerisch, Web-Parität app.js) → genau dieser Abschnitt;
      // sonst nicht-leer → Abschnitts-Slug (Chat-Link `&u=<slug>`);
      // resume=1 (CTA „Fortsetzen") → erster ungelesener; sonst Abschnitt 0.
      const isNum = /^\d+$/.test(uParam);
      const uNum = isNum ? parseInt(uParam, 10) : 0;
      const uSlug = uParam && !isNum ? uParam : undefined;
      const u = uNum > 0 ? uNum : params.get("resume") ? -1 : 0;
      const f = flatChapters(c);
      if (m && b && f.some((x) => x.m === m && x.b === b)) return { m, b, u, uSlug };
    } catch {
      /* ignore */
    }
    return null;
  }

  useEffect(() => {
    let onPop: (() => void) | null = null;
    (async () => {
      try {
        const c = await loadCourse(courseId);
        setCourse(c);
        await loadProgress(courseId).catch(() => {});
        const fromUrl = posFromUrl(c);
        if (fromUrl?.intro) {
          setIntroMod(fromUrl.intro);
          setLoading(false);
        } else if (fromUrl) {
          await selectChapter(fromUrl.m, fromUrl.b, c, false, fromUrl.u, fromUrl.uSlug);
        } else {
          const last = lastPosition();
          const flat = flatChapters(c);
          if (
            last &&
            last.courseId === courseId &&
            flat.some((f) => f.m === last.moduleId && f.b === last.blockId)
          ) {
            // Letzte Position: gespeicherter Abschnitt, sonst Resume (erster ungelesener).
            await selectChapter(last.moduleId, last.blockId, c, false, last.subIndex ?? -1);
          } else {
            // KEINE Position: Modul-Eröffnungsseite von Modul 1 — NICHT Kapitel 1 (B3).
            const first = visibleModules(c)[0];
            if (first) setIntroMod(first.id);
            setLoading(false);
          }
        }

        // Back/Forward: Position aus der URL übernehmen.
        onPop = () => {
          // Offenes Overlay (Drawer/Coach)? → Zurück schließt NUR das Overlay
          // (use-back-close übernimmt); hier keine Kapitel-Navigation auslösen.
          if (overlayIsOpen()) return;
          const p = posFromUrl(c);
          if (p?.intro) {
            if (p.intro === introModRef.current) return; // gleiche Stelle (staler Overlay-Pop)
            setIntroMod(p.intro);
            setChapter(null);
            setPos(null);
          } else if (p) {
            const cur = posRef.current;
            // Gleiche Stelle (z. B. übrig gebliebener Overlay-History-Eintrag):
            // nichts neu laden — sonst bricht Stream/Vorlesen grundlos ab.
            if (cur && cur.m === p.m && cur.b === p.b && !p.uSlug && p.u >= 0 && p.u === subIndexRef.current) return;
            void selectChapter(p.m, p.b, c, false, p.u, p.uSlug);
          }
        };
        addEventListener("popstate", onPop);
      } catch {
        setErr("Kurs konnte nicht geladen werden. Bist du angemeldet?");
        setLoading(false);
      }
    })();
    return () => {
      if (onPop) removeEventListener("popstate", onPop);
      setTtsBridge(null);
    };
  }, []);

  useEffect(() => {
    if (!chapter || !pos) return;
    scrollTopNow();
    const raf = requestAnimationFrame(scrollTopNow);
    return () => cancelAnimationFrame(raf);
  }, [chapter?.id, pos?.m, pos?.b, mode, subIndex]);

  // Werkstatt-Personalisierung (W1): nach Anpassen/Zurücksetzen/Schritt-Übernehmen
  // rechnet werkstatt-personal `wkRedraw` hoch. Pendant zu app.js wkNeuZeichnen:
  // Projekt-Segmente neu erzeugen (Overlay/Schritt-Eingaben greifen), Cache
  // auffrischen und den aktuellen Abschnitt OHNE Streaming neu zeichnen.
  useEffect(() => {
    if (wkRedraw.value === 0) return; // initialer Wert: nichts tun
    if (!chapter || !pos || chapter.layout !== "projekt") return;
    const segs = projektSegmente((chapter.raw ?? {}) as ProjektPayload, pos.m);
    segCache.set(`${pos.m}/${pos.b}`, segs);
    setSegments(segs);
    setStreamSub(false); // instant neu zeichnen (kein erneutes Streamen)
  }, [wkRedraw.value]);

  // Sprung-Brücke (Deep-Link aus dem KI-Coach): Same-Route-Fall — man ist schon
  // auf /kurs, das Coach-Sheet hat per requestJump(m, b) ein Ziel gesetzt. Hier
  // springen wir ins Kapitel (selectChapter pusht selbst History — kein Doppel-
  // Push) und setzen das Signal zurück. Greift auch, wenn der Wunsch vor dem
  // Mount kam: sobald `course` geladen ist, läuft der Effect erneut.
  useEffect(() => {
    const req = jumpRequest.value;
    if (!req || !course) return;
    // Unbekanntes Kapitel sanft abfangen: Wunsch verwerfen, nicht navigieren.
    if (!flatChapters(course).some((f) => f.m === req.m && f.b === req.b)) {
      jumpRequest.value = null;
      showToast("Dieses Kapitel gibt es hier nicht.");
      return;
    }
    jumpRequest.value = null;
    void selectChapter(req.m, req.b, course, true, 0, req.u);
  }, [jumpRequest.value, course]);

  // Modul-Eröffnungsseite immer oben starten (eigener Scroller, kein scrollRef).
  useEffect(() => {
    if (!introMod) return;
    scrollTopNow();
    const raf = requestAnimationFrame(scrollTopNow);
    return () => cancelAnimationFrame(raf);
  }, [introMod]);

  // KURSSEITE VERLASSEN (Tab-Wechsel zu Start/Profil/… → Unmount): Vorlesen
  // MUSS aufhören (Auftraggeber 12.06.: kein Audio außerhalb des Kurses).
  useEffect(() => {
    return () => {
      ttsStopp();
      setTtsBridge(null);
      setTtsPlayFallback(null);
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current); // schwebende Tipp-Erkennung verwerfen
    };
  }, []);

  // --- abgeleitete Werte ---
  const flat = flatChapters(course);
  const idx = pos ? flat.findIndex((f) => f.m === pos.m && f.b === pos.b) : -1;
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
  const mod = course?.modules.find((m) => m.id === pos?.m);
  const chapterIndex = idx + 1;
  const totalChapters = flat.length;

  const modes = course ? resolveModes(course.modes) : [];
  const questionsBlock = chapter?.blocks.find((x) => x.type === "questions") as
    | QuestionsBlock
    | undefined;
  const mediaBlocks = chapter?.blocks.filter((x) =>
    ["image", "video", "audio", "embed"].includes(x.type),
  );

  const istProjekt = chapter?.layout === "projekt";
  const werkstattIntro = !!chapter && istWerkstattIntro(mod, chapter.id);
  // Fragen-Modus hat KEINE Abschnitts-Blätterung (Web: segments=[] im Fragenmodus) —
  // sonst blättert „Weiter" durch unsichtbare Abschnitte („da passiert nichts"-Bug).
  const effSegments = mode === "fragen" && !istProjekt ? [] : segments;
  const multi = effSegments.length > 1;
  const lastSeg = effSegments.length - 1;

  const questions = questionsBlock?.questions ?? [];
  const doneCount = pos
    ? questions.filter((q) => isDone(contentKey(pos.m, pos.b, "question", q.id))).length
    : 0;
  const chapterDone = pos ? isDone(contentKey(pos.m, pos.b, "chapter", "chapter")) : false;

  // Vorlesen auf der Modul-Eröffnungsseite: ▶ springt ins erste Kapitel und liest los.
  useEffect(() => {
    if (introMod && course) {
      const m = course.modules.find((x) => x.id === introMod);
      const start = m ? modulStartChapter(m) : undefined;
      setTtsPlayFallback(m && start ? () => void selectChapter(m.id, start.id) : null);
    } else {
      setTtsPlayFallback(null);
    }
    return () => setTtsPlayFallback(null);
  }, [introMod, course]);

  // --- TTS-Bridge: der Player folgt dem Abschnitts-Stand und kann „Weiter" drücken (§5) ---
  useEffect(() => {
    if (!pos || !chapter) {
      setTtsBridge(null);
      return;
    }
    setTtsBridge({
      moduleId: pos.m,
      blockId: pos.b,
      istProjekt,
      sectionCount: effSegments.length,
      getSubIndex: () => subIndex,
      hasNext: () => (multi && subIndex < lastSeg) || !!next,
      klickWeiter: () => goNext(true),
      // Weicher Abschnittswechsel beim Überspringen über die Grenze (kein Stopp).
      goToSection: (i) => zeigeUnterkapitel(i, false),
      chapterTitle: chapter.title,
    });
  });

  function markChapterDone() {
    if (pos && !chapterDone) {
      // toggleDone prüft nach Kapitel-Abschluss selbst den Zertifikatsstatus
      // (progress.ts) → setzt ggf. das Abschluss-Modal (completion.ts certModal).
      void toggleDone({ courseId, moduleId: pos.m, blockId: pos.b, itemId: "chapter", itemType: "chapter" });
    }
  }
  /** Aktuellen Abschnitt als gelesen markieren — nur „Weiter" tut das (U3). */
  function verlasseAktuellenAbschnitt() {
    if (pos && multi && effSegments[subIndex]) markSubRead(courseId, pos.m, pos.b, effSegments[subIndex].key);
  }
  function goPrev() {
    ttsUserNav(); // manuelle Navigation stoppt das Vorlesen (Durchlese-Selbstklick = no-op)
    if (multi && subIndex > 0) {
      zeigeUnterkapitel(subIndex - 1, false); // erst Abschnitte zurück (kein Stream, U5)
      return;
    }
    if (prev) void selectChapter(prev.m, prev.b);
  }
  function goNext(self = false) {
    // Manuelles „Weiter" stoppt das Vorlesen; der Durchlese-Modus blättert per
    // Selbstklick (self) weiter und liest nahtlos — ttsUserNav ist dann no-op.
    if (!self) ttsUserNav();
    if (multi && subIndex < lastSeg) {
      verlasseAktuellenAbschnitt();
      zeigeUnterkapitel(subIndex + 1, true); // „Weiter" streamt (U5)
      return;
    }
    verlasseAktuellenAbschnitt(); // letzter Abschnitt vor Kapitelwechsel (U4)
    // Bestätigungs-Dialog nur bei MANUELLEM Weiter (nicht beim Durchlese-Selbstklick).
    if (!self && !chapterDone && !scrolledBottom()) {
      setConfirmOpen(true);
      return;
    }
    markChapterDone();
    if (next) void selectChapter(next.m, next.b);
  }
  function confirmYes() {
    setConfirmOpen(false);
    markChapterDone();
    if (next) void selectChapter(next.m, next.b);
  }
  function confirmNo() {
    setConfirmOpen(false);
    if (next) void selectChapter(next.m, next.b);
  }

  /**
   * Vorlese-Gesten auf dem Lesetext (Auftraggeber-Bedienkonzept 13.06.) — nur aktiv,
   * wenn der Audio-Hauptschalter AN ist:
   *   - EINZELTIPP auf einen Absatz → liest AB DIESEM Abschnitt (playFromSection).
   *   - DOPPELTIPP auf den Lesetext → Umschalter Pause/weiter an der gemerkten Stelle.
   * Disambiguierung über ~250 ms (TAP_DBL_MS). Interaktive Elemente (Frag-die-KI,
   * Kopieren, Aufklapper, Knöpfe, Medien) sind AUSGENOMMEN — sie behalten ihre Funktion.
   */
  const TAP_DBL_MS = 250;
  function onTextTap(ev: MouseEvent) {
    if (!ttsAudioOn.value) return; // Audio aus → Tippen löst nichts Audio-bezogenes aus
    const t = ev.target as Element | null;
    if (!t || !t.closest) return;
    if (
      t.closest(
        "button, a, summary, input, textarea, label, [data-ask-ki], [data-ask-copy], [data-copy], [data-material-download], audio, video, iframe",
      )
    )
      return;
    if (tapTimerRef.current) {
      // Zweiter Tipp innerhalb des Fensters → DOPPELTIPP = Pause/weiter-Umschalter.
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      if (ttsPlaying.value) {
        ttsPause();
        showToast("Pause");
      } else if (ttsWaiting.value) {
        ttsFortsetzen(); // an der Prompt-/Abschnittsgrenze weiter
        showToast("Vorlesen läuft weiter");
      } else if (ttsPaused.value) {
        void ttsPlay(); // an der gemerkten Stelle weiter (Resume)
        showToast("Vorlesen läuft weiter");
      } else if (pos) {
        void ttsPlay();
      }
      return;
    }
    // Erster Tipp → kurz warten; bleibt es dabei, ab diesem Abschnitt lesen.
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null;
      if (!pos || !ttsAudioOn.value) return; // Audio inzwischen ausgeschaltet → nichts tun
      ttsPlayFromSection(subIndexRef.current); // ab dem angezeigten Abschnitt
      showToast("Vorlesen ab hier");
    }, TAP_DBL_MS);
  }

  if (err && !course) {
    return (
      <div class="glass card-pad">
        <p class="err">{err}</p>
      </div>
    );
  }

  const fullTextHtml = (chapter?.blocks.find((x) => x.type === "text") as TextBlock | undefined)?.html ?? "";
  const aktSeg = effSegments[subIndex];
  const lessonHtml = multi || istProjekt ? (aktSeg?.html ?? "") : fullTextHtml;
  // Kopf-Summary nur, wenn der Lesetext NICHT wörtlich mit ihr beginnt (A17/L16).
  const zeigeSummary = !istProjekt && !summaryDoppelt(chapter?.summary, fullTextHtml);

  return (
    <div class="course-screen">
      {/* Kopf — kompakt, nur Symbole (Auftraggeber 12.06.): Menü ·
          Modus-Umschalter (Buch = Normal-Modus, ? = Fragen-Modus, nur m01) ·
          Play/Stopp fürs Vorlesen (immer, auch auf Modulseiten). */}
      <div class="course-topbar">
        <button
          class="menu-toggle"
          aria-label="Kursnavigation"
          onClick={() => {
            // Nur das gerade GELESENE Kapitel-Modul aufklappen (= „wo bin ich");
            // auf der Eröffnungsseite/ohne Position bleibt alles zu (Auftraggeber).
            if (pos?.m) setOpenMods((prev) => (prev.includes(pos.m) ? prev : [...prev, pos.m]));
            setDrawerOpen(true);
          }}
        >
          <IconMenu size={18} />
        </button>
        {hatModuswahl ? (
          <div class="course-modeswitch ms-icons" role="group" aria-label="Kurs-Modus umschalten">
            {modes.map((m) => {
              const q = m.id === "fragen";
              const label = q ? "Fragen-Modus" : "Normal-Modus";
              return (
                <button
                  key={m.id}
                  type="button"
                  class={`${q ? "q" : "r"}${mode === m.id ? " active" : ""}`}
                  aria-pressed={mode === m.id}
                  aria-label={label}
                  title={label}
                  onClick={() => setCourseMode(m.id)}
                >
                  <span class="ms-icon">{q ? "?" : <IconBook size={14} />}</span>
                </button>
              );
            })}
          </div>
        ) : null}
        <TtsQuick />
        <button
          type="button"
          class="fontsize-quick"
          aria-label={`Schriftgröße ${FONT_SCALE_SHORT[fontScalePref.value]} — tippen zum Umschalten`}
          title="Schriftgröße — tippen schaltet S · M · L · XL durch"
          onClick={() => {
            const i = FONT_SCALE_ORDER.indexOf(fontScalePref.value);
            setFontScale(FONT_SCALE_ORDER[(i + 1) % FONT_SCALE_ORDER.length]);
          }}
        >
          <span class="fs-a" aria-hidden="true">A</span>
          <span class="fs-lvl">{FONT_SCALE_SHORT[fontScalePref.value]}</span>
        </button>
      </div>

      {/* Drawer */}
      {drawerOpen ? <div class="drawer-backdrop" onClick={() => setDrawerOpen(false)} /> : null}
      <aside
        class={`drawer${drawerOpen ? " open" : ""}`}
        aria-hidden={!drawerOpen}
        style={`background:linear-gradient(180deg, rgba(14,42,84,.88), rgba(5,20,48,.97)), url('${assetUrl("bg14-flip-y.png")}') center / cover no-repeat, rgba(5,20,48,.98)`}
      >
        <div class="drawer-head">
          <strong style="font-family:var(--font-head);font-size:18px">Kursnavigation</strong>
          <button class="drawer-close" aria-label="Schließen" onClick={() => setDrawerOpen(false)}>
            <IconClose size={18} />
          </button>
        </div>

        {/* „Start"-Menü wie im Desktop-Header (Startseite + Unterpunkte) */}
        <nav class="drawer-start" aria-label="Start">
          <button type="button" class="drawer-start-link" onClick={() => { setDrawerOpen(false); route("/"); }}>
            <IconHome size={16} /> Startseite
          </button>
          <button type="button" class="drawer-start-link" onClick={() => { setDrawerOpen(false); route("/bibliothek"); }}>
            <IconLibrary size={16} /> Downloads
          </button>
          <button type="button" class="drawer-start-link" onClick={() => { setDrawerOpen(false); openSheet(); }}>
            <IconSparkles size={16} /> KI-Coach
          </button>
        </nav>

        {/* Modus-Panel (nur m01) + Vorlesen-Knopfreihe (immer, wie Web A1) */}
        {hatModuswahl ? (
          <section class="mode-panel" aria-label="Kurs Modus">
            <div class="mode-title">
              <span class="mode-title-icon"><IconSparkles size={14} /></span>
              <span>Kurs Modus</span>
              <button class="mode-info" aria-label="Was bedeuten die Kurs-Modi?" onClick={() => setInfoOpen(true)}>
                i
              </button>
            </div>
            <div class="modetabs">
              {modes.map((m) => (
                <button
                  key={m.id}
                  class={`modetab ${m.id === "fragen" ? "questions" : "reading"}${mode === m.id ? " active" : ""}`}
                  onClick={() => setCourseMode(m.id)}
                >
                  <span class="mt-icon">{m.id === "fragen" ? "?" : "▦"}</span>
                  {m.label}
                </button>
              ))}
            </div>
            <p class="mode-hint">{getMode(mode)?.hint}</p>
          </section>
        ) : null}

        {/* Navigationsbaum — Web-Muster S1: Name-Button + Pfeil-Toggle, kein details */}
        {visibleModules(course).map((m) => {
          const offen = moduleOpen(m.id);
          return (
            <div key={m.id} class={`navmod${offen ? " open" : ""}`}>
              <div class="navmod-head">
                <button
                  type="button"
                  class="navmod-name"
                  title="Modul-Eröffnungsseite öffnen"
                  onClick={() => openIntro(m.id)}
                >
                  {modLabel(m)}
                </button>
                <button
                  type="button"
                  class="navmod-toggle"
                  aria-label="Kapitel ein-/ausklappen"
                  aria-expanded={offen}
                  onClick={() => toggleModule(m.id)}
                >
                  <span class="chev" aria-hidden="true">▶</span>
                </button>
              </div>
              {offen ? (
                <div class="blocks">
                  {m.chapters.map((ch) => {
                    const done = isDone(contentKey(m.id, ch.id, "chapter", "chapter"));
                    const active = pos?.m === m.id && pos?.b === ch.id;
                    const segs = segCache.get(`${m.id}/${ch.id}`) ?? [];
                    const dots =
                      segs.length > 1 ? (
                        <div class="uk-dots uk-dots-side">
                          {segs.map((s, k) => (
                            <button
                              key={s.key}
                              type="button"
                              class={`uk-dot${isSubRead(m.id, ch.id, s.key) ? " read" : ""}${active && k === subIndex ? " active" : ""}`}
                              title={s.title || `Abschnitt ${k + 1}`}
                              aria-label={`${ch.title} – Abschnitt ${k + 1} von ${segs.length}`}
                              onClick={() => {
                                ttsUserNav(); // Sidebar-Klick stoppt das Vorlesen
                                if (active) {
                                  zeigeUnterkapitel(k, false);
                                  setDrawerOpen(false);
                                } else void selectChapter(m.id, ch.id, course, true, k);
                              }}
                            />
                          ))}
                        </div>
                      ) : null;
                    return (
                      <div key={ch.id} class="navblock">
                        <button
                          type="button"
                          class={`${active ? "active " : ""}${done ? "done" : ""}`}
                          onClick={() => {
                            ttsUserNav(); // Sidebar-Klick stoppt das Vorlesen
                            void selectChapter(m.id, ch.id);
                          }}
                        >
                          <span class="dot" />
                          {ch.title}
                        </button>
                        {dots}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </aside>

      {/* Info-Sheet zu den Modi */}
      {infoOpen ? (
        <Modal onClose={() => setInfoOpen(false)}>
          <h2>Die zwei Kurs-Modi</h2>
          <p style="text-align:left;margin-bottom:10px">
            <strong style="color:var(--gold)">Kurs über Fragen:</strong> Du lernst die
            Kursinhalte, indem du der KI die richtigen Fragen stellst.
          </p>
          <p style="text-align:left">
            <strong style="color:var(--teal)">Kapitelmodus:</strong> Lies den Kurs ganz
            normal Kapitel nach Kapitel durch und stelle deine Fragen dem Chatbot.
          </p>
          <div class="chapter-confirm-actions">
            <button class="btn-coach" onClick={() => setInfoOpen(false)}>Verstanden</button>
          </div>
        </Modal>
      ) : null}

      {/* Modul-Eröffnungsseite (Intro) */}
      {introMod ? (
        <ModuleIntro
          mod={course?.modules.find((x) => x.id === introMod) ?? null}
          onStart={(m, b) => void selectChapter(m, b)}
        />
      ) : null}

      {/* Kapitelplayer */}
      {!introMod ? (
      <section
        class={`chapter-player glass${istProjekt ? " chapter-projekt" : ""}`}
        style={chapter?.image ? `--chapter-bg:url('${chapter.image}')` : undefined}
      >
        <header class="chapter-header">
          {/* Chip „Teilnehmer/Trainer" entfernt (Web-Parität 215e49d). */}
          <p class="chapter-kicker">
            {modNumPrefix(mod)}{mod?.title} · Kapitel {chapterIndex}/{totalChapters}
          </p>
          <h1>{chapter?.title ?? (loading ? "Lädt …" : "Kapitel")}</h1>
          {chapter?.summary && (mode === "normal" || istProjekt) && (istProjekt || zeigeSummary) ? (
            <p class="chapter-summary">{chapter.summary}</p>
          ) : null}
          <div class="chapter-header-tools">
            {questions.length && !istProjekt ? (
              <div class="progress-row">
                <div class="bar">
                  <i style={`width:${questions.length ? (doneCount / questions.length) * 100 : 0}%`} />
                </div>
                <span>{doneCount}/{questions.length}</span>
              </div>
            ) : null}
            {/* Token-Balken auch beim Lesen (Web-Parität: im Web steht er in der
                persistenten Kopfzeile, also auch auf modul.html). */}
            <TokenBalken />
          </div>
        </header>

        <div class="chapter-scroll" ref={scrollRef} onClick={onTextTap}>
          {err ? <p class="err">{err}</p> : null}
          {mediaBlocks && mediaBlocks.length && pos && !istProjekt ? (
            <div class="lesson-media-grid">
              {mediaBlocks.map((b) => (
                <MediaFigure key={b.id} block={b} ctx={{ courseId, moduleId: pos.m, blockId: pos.b }} />
              ))}
            </div>
          ) : null}

          {mode === "fragen" && !istProjekt ? (
            <>
              <div class="callout-nb glass">
                <span class="ico">◆</span>
                <div>
                  {chapter?.summary ? (
                    <>
                      <h4>Darum geht's in diesem Kapitel</h4>
                      <p>{chapter.summary}</p>
                    </>
                  ) : (
                    <h4>Stell diese Fragen deiner Kurs-KI</h4>
                  )}
                  <p>
                    <strong>So lernst du es hier:</strong> Du liest keinen Fließtext — du stellst
                    die Fragen unten deiner Kurs-KI, eine nach der anderen. Lies den
                    Antwort-Hinweis erst, nachdem die KI geantwortet hat — und vergleiche.
                  </p>
                </div>
              </div>
              {questions.length && pos ? (
                questions.map((q, i) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    index={i + 1}
                    courseId={courseId}
                    moduleId={pos.m}
                    blockId={pos.b}
                  />
                ))
              ) : (
                <div class="lesson glass">
                  <p>Für dieses Kapitel gibt es keine Fragen. Nutze den Lesemodus.</p>
                </div>
              )}
            </>
          ) : (
            <>
              {chapter && pos ? (
                <LessonStream
                  key={`${pos.m}-${pos.b}-${multi || istProjekt ? subIndex : "voll"}`}
                  html={lessonHtml}
                  instant={istProjekt || !streamSub}
                  ctx={{ courseId, moduleId: pos.m, blockId: pos.b }}
                />
              ) : null}
              {werkstattIntro && mod && pos ? (
                <WerkstattUebersicht
                  courseId={courseId}
                  mod={mod}
                  onOpen={(m, b) => void selectChapter(m, b)}
                />
              ) : null}
            </>
          )}
        </div>

        <footer class="chapter-footer">
          <div class="chapter-footer-nav">
            <button class="btn-ghost chapter-nav-btn" onClick={goPrev} disabled={!prev && !(multi && subIndex > 0)}>
              <IconArrowLeft size={16} /> Zurück
            </button>
            {multi ? (
              <span class="chapter-section-indicator" aria-live="polite">
                Abschnitt {subIndex + 1}/{effSegments.length}
              </span>
            ) : null}
            <button class="chapter-next" onClick={() => goNext()} disabled={!next && !(multi && subIndex < lastSeg)}>
              {multi && subIndex < lastSeg ? "Weiter" : "Nächstes Kapitel"} <IconArrowRight size={16} />
            </button>
          </div>
          {multi && pos ? (
            <div class="uk-dots uk-dots-footer" aria-label="Abschnitte dieses Kapitels">
              {effSegments.map((s, k) => (
                <button
                  key={s.key}
                  type="button"
                  class={`uk-dot${isSubRead(pos.m, pos.b, s.key) ? " read" : ""}${k === subIndex ? " active" : ""}`}
                  title={s.title || `Abschnitt ${k + 1}`}
                  aria-label={`Abschnitt ${k + 1} von ${effSegments.length}`}
                  aria-current={k === subIndex ? "step" : undefined}
                  onClick={() => {
                    if (k === subIndex) return;
                    // Abschnitt im SELBEN Kapitel anklicken (13.06. Punkt 4): läuft/
                    // pausiert das Vorlesen, ab DIESEM Abschnitt weiterlesen — sonst nur
                    // hin-navigieren (kein Audio).
                    const liesWeiter = ttsAudioOn.value && (ttsPlaying.value || ttsWaiting.value || ttsPaused.value);
                    zeigeUnterkapitel(k, false);
                    if (liesWeiter) ttsPlayFromSection(k);
                    else ttsUserNav();
                  }}
                />
              ))}
            </div>
          ) : null}
          <label class="chapter-done">
            <input type="checkbox" checked={chapterDone} disabled />
            <span>Kapitel erledigt</span>
          </label>
        </footer>
      </section>
      ) : null}

      {/* Erledigt-Modal */}
      {confirmOpen ? (
        <Modal onClose={confirmNo}>
          <h2>Kapitel als erledigt markieren?</h2>
          <p>Du bist noch nicht bis zum Ende dieses Kapitels gescrollt.</p>
          <div class="chapter-confirm-actions">
            <button class="btn-ghost" onClick={confirmNo}>Nein</button>
            <button class="btn-coach" onClick={confirmYes}>Ja</button>
          </div>
        </Modal>
      ) : null}

      {/* Scroll-Hinweis (P8): fix über der Tab-Leiste, da das Fenster scrollt. */}
      {showScrollHint ? (
        <div class="scroll-hinweis-fixed" aria-hidden="true">
          <div class="scroll-hinweis">
            <span>⌄</span>
            <span>⌄</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
